<script lang="ts">
import { onMount } from 'svelte'
import { fly } from 'svelte/transition'

import { isMessengerFrameOpen } from '@/modules/app'
import { isFullScreenEnabled } from '@/modules/device'
import { hasContactOrAgentMessage, isMessengerInputDisabled } from '@/modules/messages'
import { widgetOptions } from '@/modules/options'
import { setToStorage, StorageItem } from '@/modules/storage'
import { isMessengerInputEmpty, messengerInputValue, sendMessageWithAuthCheck } from '@/modules/textarea'
import { debounce } from '@/utils/debounce'
import { getHostWindow } from '@/utils/window'

import Attachments from './WidgetMessengerAttachments.svelte'
import AutosizeTextarea from './WidgetMessengerAutosizeTextarea.svelte'
import WidgetMessengerEmojiButton from './WidgetMessengerEmojiButton.svelte'
import WidgetMessengerPrimaryButton from './WidgetMessengerPrimaryButton.svelte'

let textareaRef: HTMLTextAreaElement | null = $state(null)

const { widgetBlockingOptions, mobileSdk } = widgetOptions.getOptions()

onMount(() => {
	const handleWindowUnload = () => {
		setToStorage({ name: StorageItem.Message, value: $messengerInputValue })
	}

	getHostWindow().addEventListener('pagehide', handleWindowUnload)
	return () => getHostWindow().removeEventListener('pagehide', handleWindowUnload)
})

// Handles the case of saving the message to storage when back/forward cache flag is enabled, or mobile SDK is used
const debouncedSaveToStore = debounce((text: string) => setToStorage({ name: StorageItem.Message, value: text }), 500)
$effect(() => {
	if (getHostWindow().SMARTSUPP_BFCACHE === true || mobileSdk) {
		debouncedSaveToStore($messengerInputValue)
	}
})

let isMessengerInput = $derived($isMessengerFrameOpen)

let shouldRenderPrimaryButton = $derived.by(() => {
	if (isMessengerInput) {
		return $hasContactOrAgentMessage || !$isMessengerInputEmpty
	} else {
		return true
	}
})
</script>

{#if !$isMessengerInputDisabled && !widgetBlockingOptions?.disableTextarea}
	<div class="relative flex items-end">
		<div
			transition:fly={{ y: 15, duration: 500 }}
			class="flex flex-col bg-slate-100 rounded-3xl transition-width-250 focus-within:outline-2 focus-within:outline-primary-focus focus-within:outline-solid"
			style:width={shouldRenderPrimaryButton ? 'calc(100% - 56px)' : '100%'}
		>
			{#if isMessengerInput}
				<Attachments />
			{/if}
			<div class="flex w-full">
				<AutosizeTextarea bind:textareaRef onKeyEnter={sendMessageWithAuthCheck} resizable={isMessengerInput} />
				{#if isMessengerInput}
					<div class="flex-shrink flex items-end text-lg text-slate-500" class:mb-[2px]={$isFullScreenEnabled}>
						<WidgetMessengerEmojiButton {textareaRef} />
					</div>
				{/if}
			</div>
		</div>
		{#if shouldRenderPrimaryButton}
			<WidgetMessengerPrimaryButton />
		{/if}
	</div>
{/if}
